import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './ConfirmDialog.scss';

/** Lightweight confirm modal used for quitting a quiz and for switching site
 * halves mid-flow. Escape and the Stay button dismiss; focus lands on Stay. */
export default function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  // Portal to <body>: the dialog is rendered from inside <header>, which carries
  // a `view-transition-name` (→ its own stacking context), so an in-place
  // z-index can't rise above the footer / `main` comboboxes (each in a later
  // stacking context). Portaling escapes the header subtree entirely.
  return createPortal(
    <div className="confirm-overlay">
      <div className="confirm" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title">
        <h2 id="confirm-title" className="confirm__title">
          {title}
        </h2>
        <p className="confirm__body">{body}</p>
        <div className="confirm__actions">
          <button
            ref={cancelRef}
            type="button"
            className="confirm__btn confirm__btn--ghost"
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
          <button type="button" className="confirm__btn confirm__btn--primary" onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
